import React, { FC, HTMLAttributes, ReactNode, useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { ClickableContainer } from 'components/clickable-container';
import { LinkButton } from 'components/link-button';
import { Arrow } from 'components/selection-container/arrow';
import VisuallyHidden from 'components/visually-hidden';
import { useTabNavigationWatcher } from 'state/accessibility/use-tab-navigation-watcher.ts';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isButtonLikeKeypress } from 'utils/event';
import { filterProps } from 'utils/filter-props';
import { screenReaderOnly } from 'utils/style/screen-reader-only';

import { RewardsStyleVariant, WithStyleVariant } from '../types';

import { CategoryContainer } from './category-container';

type CategoryDropdownProps = {
  children: ReactNode;
  focusOptionsWhenNotVisible?: boolean;
  id: string;
  label: string;
  onClickSeeMoreActions?: () => void;
  optionsVisible: boolean;
  overlay?: boolean;
  selectedOptionName?: string;
  title: ReactNode;
  toggleOptionsVisible: () => void;
} & WithStyleVariant &
  Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

const noBottomBorder = css`
  border-bottom: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;
const CategoryDropdownContainer = styled(CategoryContainer)<
  Pick<CategoryDropdownProps, 'optionsVisible' | 'overlay'>
>`
  position: relative;
  width: 100%;
  padding: 0;
  ${({ optionsVisible, overlay }) => overlay && optionsVisible && noBottomBorder};
`;

const TitleContainer = styled.div`
  padding: 0.5rem 1rem 0;
  width: 100%;
`;

const Title = styled(ClickableContainer)<
  { isUsingTabNavigation: boolean; showBorder: boolean } & WithStyleVariant
>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${({ showBorder }) => (showBorder ? '1px' : '0px')};
  border-bottom-style: solid;
  border-bottom-color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? '#880016' : Styles.color.grey.four};
  &: focus {
    ${({ isUsingTabNavigation }) => !isUsingTabNavigation && 'outline: none;'};
  }
  width: 100%;
  padding-bottom: 0.5rem;

  & > {
    height: 3rem;
  }
`;

type OptionsContainerProps = Pick<
  CategoryDropdownProps,
  'focusOptionsWhenNotVisible' | 'optionsVisible' | 'overlay'
> &
  WithStyleVariant;

const overlayStyle = css<OptionsContainerProps>`
  position: absolute;
  width: calc(100% + 2px);
  left: -1px;
  background-color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? '#990019' : Styles.color.white};
  z-index: ${Styles.zIndex.below};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? '#990019' : Styles.color.grey.four};
  border-top: none;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
`;

const OptionsContainer = styled.div<OptionsContainerProps>`
  ${({ overlay }) => overlay && overlayStyle}
  padding: 0 1rem;
  z-index: ${Styles.zIndex.normal};
  ${({ focusOptionsWhenNotVisible, optionsVisible }) =>
    !optionsVisible && (focusOptionsWhenNotVisible ? screenReaderOnly : 'display: none;')};
`;

const StyledArrow = styled(Arrow)`
  margin-right: 0.45rem;
  margin-left: 0.5rem;
`;

const SeeMoreOptionsCTA = styled(filterProps(LinkButton, 'variant'))<WithStyleVariant>`
  width: 100%;
  padding: 1.5rem 0 2rem;
  text-align: left;
  font-size: 0.875rem;
  color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.grey.two};
`;

const messages = defineMessages({
  containsSelectedOption: {
    id: 'containsSelectedOption',
    defaultMessage: ' contains selected level {rewardTier}',
    description: 'Lets screen reader user know when category contains selected option'
  },
  rewardCategory: {
    id: 'rewardCategory',
    defaultMessage: '{categoryName} reward levels{selectedOptionText}',
    description: 'Title for dropdown containing reward level options'
  },
  seeMoreOptions: {
    id: 'seeMoreOptions',
    defaultMessage: 'See more options',
    description: 'See more options button text'
  }
});

// TODO: consolidate with other solutions (TextExpander, SelectionContainer) in the code base, extract fieldset
export const CategoryDropdown: FC<CategoryDropdownProps> = ({
  children,
  focusOptionsWhenNotVisible = false,
  id,
  label,
  onClickSeeMoreActions,
  optionsVisible,
  overlay = false,
  selectedOptionName,
  title,
  toggleOptionsVisible,
  variant,
  ...props
}) => {
  const { isUsingTabNavigation } = useTabNavigationWatcher();

  const loyalty2SeeMoreOptionsEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_LOYALTY_2_0_SEE_MORE_OPTIONS
  );
  const { formatMessage } = useIntl();

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isButtonLikeKeypress(e)) {
        e.preventDefault();
        toggleOptionsVisible();
      }
    },
    [toggleOptionsVisible]
  );

  return (
    <CategoryDropdownContainer optionsVisible={optionsVisible} overlay={overlay} variant={variant}>
      <TitleContainer>
        <Title
          aria-controls={`${id}-content`}
          aria-expanded={optionsVisible}
          isUsingTabNavigation={isUsingTabNavigation}
          onClick={toggleOptionsVisible}
          onKeyDown={onKeyDown}
          showBorder={optionsVisible}
          tabIndex={0}
          variant={variant}
        >
          <VisuallyHidden>
            {formatMessage(messages.rewardCategory, {
              categoryName: label,
              selectedOptionText: selectedOptionName
                ? formatMessage(messages.containsSelectedOption, {
                    rewardTier: selectedOptionName
                  })
                : ''
            })}
          </VisuallyHidden>
          <span aria-hidden>{title}</span>
          <StyledArrow
            aria-hidden
            isOpen={optionsVisible}
            fill={
              variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.grey.one
            }
          />
        </Title>
      </TitleContainer>
      <OptionsContainer
        {...props}
        id={`${id}-content`}
        focusOptionsWhenNotVisible={focusOptionsWhenNotVisible}
        optionsVisible={optionsVisible}
        overlay={overlay}
        variant={variant}
      >
        {children}
        {loyalty2SeeMoreOptionsEnabled && onClickSeeMoreActions && optionsVisible && (
          <SeeMoreOptionsCTA onClick={onClickSeeMoreActions} variant={variant}>
            {formatMessage(messages.seeMoreOptions)}
          </SeeMoreOptionsCTA>
        )}
      </OptionsContainer>
    </CategoryDropdownContainer>
  );
};
